import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const PublicComplaint = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const preSelectedAreaId = searchParams.get('area_id') || '';
  const preSelectedDeptId = searchParams.get('department_id') || '';

  const [departments, setDepartments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [subAreas, setSubAreas] = useState([]);
  const [selectedAreaInfo, setSelectedAreaInfo] = useState(null);

  const [formData, setFormData] = useState({
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    title: '',
    description: '',
    category: 'General',
    priority: 'Medium',
    department_id: preSelectedDeptId,
    area_id: preSelectedAreaId,
    sub_area_id: ''
  });

  const [complaintImage, setComplaintImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submittedTicket, setSubmittedTicket] = useState(null);

  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      const [deptRes, areaRes] = await Promise.all([
        api.get('/departments'),
        api.get('/areas')
      ]);
      setDepartments(deptRes.data);
      setAreas(areaRes.data);

      if (preSelectedAreaId) {
        const foundArea = areaRes.data.find(a => String(a.id) === String(preSelectedAreaId));
        if (foundArea) setSelectedAreaInfo(foundArea);
        fetchSubAreas(preSelectedAreaId);
      }
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  const fetchSubAreas = async (areaId) => {
    if (!areaId) {
      setSubAreas([]);
      return;
    }
    try {
      const res = await api.get(`/sub-areas?area_id=${areaId}`);
      setSubAreas(res.data);
    } catch (err) {
      console.error('Error fetching sub-areas:', err);
    }
  };

  const handleAreaChange = (e) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, area_id: val, sub_area_id: '' }));
    const foundArea = areas.find(a => String(a.id) === String(val));
    setSelectedAreaInfo(foundArea || null);
    fetchSubAreas(val);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          data.append(key, formData[key]);
        }
      });
      if (complaintImage) {
        data.append('complaint_image', complaintImage);
      }

      const res = await api.post('/complaints/public', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSubmittedTicket(res.data.complaint);
    } catch (err) {
      console.error('Public Submit Error:', err);
      setError(err.response?.data?.message || 'Failed to submit complaint. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const copyTicket = () => {
    if (submittedTicket) {
      navigator.clipboard.writeText(submittedTicket.ticket_number);
      alert(t('ticketCopiedAlert'));
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card glass-card border-0 p-4 p-md-5">
            <div className="text-center mb-4">
              <span className="badge bg-primary fs-6 px-3 py-2 text-uppercase mb-2">{t('publicCitizenDesk')}</span>
              <h2 className="brand-font fw-bold text-dark">{t('registerPublicComplaint')}</h2>
              <p className="text-muted">{t('publicDeskSubtitle')}</p>
            </div>

            {/* Visual Step Wizard Indicator */}
            {!submittedTicket && (
              <div className="d-flex justify-content-between align-items-center mb-4 px-3 py-2 bg-light rounded-pill border">
                <div className="d-flex align-items-center gap-2">
                  <div className="step-pill active">1</div>
                  <span className="fw-bold small text-dark d-none d-sm-inline">{t('stepContactDetails')}</span>
                </div>
                <i className="bi bi-chevron-right text-muted small"></i>
                <div className="d-flex align-items-center gap-2">
                  <div className="step-pill active">2</div>
                  <span className="fw-bold small text-dark d-none d-sm-inline">{t('stepWardLocation')}</span>
                </div>
                <i className="bi bi-chevron-right text-muted small"></i>
                <div className="d-flex align-items-center gap-2">
                  <div className="step-pill active">3</div>
                  <span className="fw-bold small text-dark d-none d-sm-inline">{t('stepIssueDescription')}</span>
                </div>
              </div>
            )}

            {selectedAreaInfo && (
              <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
                <i className="bi bi-geo-alt-fill fs-3 me-3 text-info"></i>
                <div>
                  <h6 className="fw-bold mb-1">{t('registeredWard')}: {selectedAreaInfo.name} ({selectedAreaInfo.code})</h6>
                  {selectedAreaInfo.representatives && selectedAreaInfo.representatives.length > 0 ? (
                    <div className="small text-muted mt-1">
                      <strong className="text-dark d-block">{t('electedWardOfficials')}:</strong>
                      {selectedAreaInfo.representatives.map(r => (
                        <span key={r.id} className="badge bg-light text-dark border me-1 my-1">
                          <i className="bi bi-person-badge text-primary me-1"></i>
                          {r.name} ({r.designation}) {r.contact_info ? `[${r.contact_info}]` : ''}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mb-0 small text-muted">{t('noWardOfficials')}</p>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i> {error}
              </div>
            )}

            {submittedTicket ? (
              <div className="text-center py-4">
                <div className="d-inline-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success rounded-circle p-4 mb-3">
                  <i className="bi bi-check-circle-fill fs-1"></i>
                </div>
                <h3 className="brand-font fw-bold text-success mb-2">{t('complaintSubmittedSuccess')}</h3>
                <p className="text-muted">{t('complaintLoggedNotice')}</p>

                <div className="bg-light p-4 rounded-3 border d-inline-block my-3 shadow-sm">
                  <div className="text-muted extra-small text-uppercase fw-bold mb-1">{t('officialTicketRef')}</div>
                  <div className="display-6 font-monospace fw-bold text-primary">{submittedTicket.ticket_number}</div>
                  <button onClick={copyTicket} className="btn btn-sm btn-outline-primary mt-2">
                    <i className="bi bi-clipboard me-1"></i> {t('copyTicketNumber')}
                  </button>
                </div>

                <div className="alert alert-warning small text-start my-4">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  {t('saveTicketNotice')}
                </div>

                <div className="d-flex justify-content-center gap-3">
                  <Link to={`/track-complaint?ticket=${submittedTicket.ticket_number}`} className="btn btn-primary-gradient px-4">
                    <i className="bi bi-search me-1"></i> {t('trackTicketStatusNow')}
                  </Link>
                  <button onClick={() => setSubmittedTicket(null)} className="btn btn-outline-secondary px-4">
                    {t('registerAnotherComplaint')}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">
                  <i className="bi bi-person-circle me-2 text-primary"></i> {t('citizenContactInfo')}
                </h5>

                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('fullName')} <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="guest_name"
                      className="form-control"
                      placeholder="e.g. Rahul Sharma"
                      value={formData.guest_name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('mobilePhone')}</label>
                    <input
                      type="tel"
                      name="guest_phone"
                      className="form-control"
                      placeholder="e.g. +91 98200 12345"
                      value={formData.guest_phone}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-12">
                    <label className="form-label fw-semibold">{t('emailAddress')}</label>
                    <input
                      type="email"
                      name="guest_email"
                      className="form-control"
                      placeholder="name@example.com"
                      value={formData.guest_email}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">
                  <i className="bi bi-journal-text me-2 text-primary"></i> {t('locationComplaintInfo')}
                </h5>

                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('selectAreaWard')}</label>
                    <select
                      name="area_id"
                      className="form-select"
                      value={formData.area_id}
                      onChange={handleAreaChange}
                    >
                      <option value="">{t('chooseAreaWard')}</option>
                      {areas.map(a => (
                        <option key={a.id} value={a.id}>{a.name} ({a.code})</option>
                      ))}
                    </select>
                  </div>

                  {/* Cascading Sub-Area Selector */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('subAreaLocation')}</label>
                    <select
                      name="sub_area_id"
                      className="form-select"
                      value={formData.sub_area_id}
                      onChange={handleChange}
                      disabled={!formData.area_id || subAreas.length === 0}
                    >
                      <option value="">
                        {!formData.area_id 
                          ? t('selectAreaFirst') 
                          : subAreas.length === 0 
                          ? t('noSubAreasDefined') 
                          : t('chooseSubArea')}
                      </option>
                      {subAreas.map(sa => (
                        <option key={sa.id} value={sa.id}>{sa.name} ({sa.code || 'Location'})</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('targetDepartment')}</label>
                    <select
                      name="department_id"
                      className="form-select"
                      value={formData.department_id}
                      onChange={handleChange}
                    >
                      <option value="">{t('chooseDepartment')}</option>
                      {departments.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('priorityLevel')}</label>
                    <select
                      name="priority"
                      className="form-select"
                      value={formData.priority}
                      onChange={handleChange}
                    >
                      <option value="Low">{t('priority_Low')} (72h SLA)</option>
                      <option value="Medium">{t('priority_Medium')} (48h SLA)</option>
                      <option value="High">{t('priority_High')} (24h SLA)</option>
                    </select>
                  </div>

                  <div className="col-md-12">
                    <label className="form-label fw-semibold">{t('complaintSubject')} <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="title"
                      className="form-control"
                      placeholder={t('subjectPlaceholder')}
                      value={formData.title}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-12">
                    <label className="form-label fw-semibold">{t('detailedDescription')} <span className="text-danger">*</span></label>
                    <textarea
                      name="description"
                      className="form-control"
                      rows="4"
                      placeholder={t('descriptionPlaceholder')}
                      value={formData.description}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="col-md-12">
                    <label className="form-label fw-semibold text-primary">
                      <i className="bi bi-camera-fill me-1"></i> {t('attachComplaintPhoto')}
                    </label>
                    <input
                      type="file"
                      className="form-control border-primary"
                      accept="image/*"
                      onChange={(e) => setComplaintImage(e.target.files[0] || null)}
                    />
                    <small className="text-muted extra-small">
                      {t('photoHelpText')}
                    </small>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary-gradient w-100 py-3 font-monospace fw-bold fs-5" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {t('submittingTicket')}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send-fill me-2"></i> {t('submitTicket')}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicComplaint;
